import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCartItemCount } from '../services/cartService';
import { useAuth } from '../contexts/AuthContext';

interface CartButtonProps {
  variant?: 'icon' | 'button';
  size?: 'sm' | 'md' | 'lg';
  showCount?: boolean;
  isActive?: boolean;
  className?: string;
}

const CartButton: React.FC<CartButtonProps> = ({
  variant = 'icon',
  size = 'md',
  showCount = true,
  isActive = false,
  className = '',
}) => {
  const [cartCount, setCartCount] = useState(0);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Don't make any API calls if not authenticated
    if (!isAuthenticated) {
      setCartCount(0);
      return;
    }

    // Initial cart count - only if authenticated
    updateCartCount();

    // Set up interval to refresh cart count only when authenticated
    const intervalId = setInterval(() => {
      if (isAuthenticated) {
        updateCartCount();
      } else {
        setCartCount(0);
      }
    }, 5000); // Increased interval to reduce API calls

    return () => clearInterval(intervalId);
  }, [isAuthenticated]); // Re-run effect when auth state changes

  const updateCartCount = async () => {
    try {
      const count = await getCartItemCount();
      setCartCount(count);
    } catch (error) {
      console.error('Error fetching cart:', error);
      setCartCount(0);
    }
  };

  // Size classes
  const sizeClasses = {
    sm: {
      icon: 'w-4 h-4',
      button: 'text-xs px-2 py-1',
      badge: 'w-3.5 h-3.5 text-[9px]',
    },
    md: {
      icon: 'w-5 h-5',
      button: 'text-sm px-3 py-1.5',
      badge: 'w-4 h-4 text-[10px]',
    },
    lg: {
      icon: 'w-6 h-6',
      button: 'text-base px-4 py-2',
      badge: 'w-5 h-5 text-[11px]',
    }
  };

  // Icon-only variant
  if (variant === 'icon') {
    return (
      <Link
        to="/cart"
        className={`relative ${className}`}
      >
        <svg
          className={`${sizeClasses[size].icon} ${isActive ? 'text-white' : 'text-[var(--color-muted)]'} hover:text-white transition-colors`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        {showCount && cartCount > 0 && (
          <div className={`absolute -top-1.5 -right-1.5 flex items-center justify-center ${sizeClasses[size].badge} rounded-full bg-[#F670C7] text-white`}>
            {cartCount}
          </div>
        )}
      </Link>
    );
  }

  // Button variant
  return (
    <Link
      to="/cart"
      className={`flex items-center gap-2 ${isActive ? 'bg-[#7F56D9]' : 'bg-[#2A2633] hover:bg-[#3A3643]'} text-white rounded-md transition-colors ${sizeClasses[size].button} ${className}`}
    >
      <svg
        className={`${size === 'sm' ? 'w-3 h-3' : size === 'lg' ? 'w-5 h-5' : 'w-4 h-4'}`}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M9 22C9.55228 22 10 21.5523 10 21C10 20.4477 9.55228 20 9 20C8.44772 20 8 20.4477 8 21C8 21.5523 8.44772 22 9 22Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M20 22C20.5523 22 21 21.5523 21 21C21 20.4477 20.5523 20 20 20C19.4477 20 19 20.4477 19 21C19 21.5523 19.4477 22 20 22Z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        <path d="M1 1H5L7.68 14.39C7.77144 14.8504 8.02191 15.264 8.38755 15.5583C8.75318 15.8526 9.2107 16.009 9.68 16H19.4C19.8693 16.009 20.3268 15.8526 20.6925 15.5583C21.0581 15.264 21.3086 14.8504 21.4 14.39L23 6H6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      </svg>
      <span>Cart</span>
      {showCount && cartCount > 0 && (
        <div className="flex items-center justify-center px-1.5 py-0.5 text-xs rounded-full bg-[#F670C7] text-white">
          {cartCount}
        </div>
      )}
    </Link>
  );
};

export default CartButton;
